/**
 * @file Every Signify callable, one thin wrapper each. Consumers: the `useSignifyApi` hooks in
 * `../api/` and, through them, the configuration sidebar and the fetch-job modal.
 * The invariant these wrappers exist to hold: a failure is logged here, where the context is, and
 * then the ORIGINAL error is rethrown. Wrapping it in a new `Error` was what stripped an
 * `HttpsError`'s `code` before `useSignifyApi.execute` ever saw it, leaving callers with a message
 * they had to parse. Callers that need a sentence of their own build it from the code.
 * `searchProducts` is the one deliberate exception: an empty result list is a usable answer for a
 * search box, so it swallows and substitutes rather than throwing.
 * The file is 4-space indented against the repo's 2; leave it that way rather than reflowing it.
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../services/firebaseFunctions';
import {
    ProductLookupRequest,
    ProductSearchRequest,
    ProductSearchResponse, 
    ProductLookupResponse,
    ProductAndPriceAndAvailabilityResponse,
    GetSignifyCustomersRequest,
    SignifyCustomersResponse,
    GetSignifyDistributorsRequest,
    SignifyDistributorsResponse,
    GetSignifyProductAvailabilityRequest,
    SignifyProductAvailabilityResponse,
    GetSignifyDistributorDetailsRequest,
    SignifyDistributorDetails,
    GetConfiguredProductsRequest,
    SignifyConfiguredProductsResponse,
    GetConfigurationSessionRequest,
    SignifyConfigurationSessionResponse,
    AutoConfigureProductRequest,
    SignifyAutoConfigureResponse,
    GetSignifyProductPriceRequest,
    SignifyProductPriceResponse,
    CreateQuoteRequest,
    SignifyQuoteResponse,
    GetQuoteRequest,
    SignifyQuoteDetailsResponse,
    IssueSignifyTokenRequest,
    CheckSignifyOrderStatusRequest,
    SignifyOrderStatusCheck,
    StartSignifyFetchJobRequest,
    StartSignifyFetchJobResponse,
    RetrySignifyFetchJobRequest,
    RetrySignifyFetchJobResponse,
    CancelSignifyFetchJobRequest,
    CancelSignifyFetchJobResponse
} from '#types';

export const searchProducts = async (searchParams: ProductSearchRequest): Promise<ProductSearchResponse> => {
    try {
        const searchSignifyProducts = httpsCallable<ProductSearchRequest, ProductSearchResponse>(
            functions,
            'searchSignifyProducts'
        );
        
        const result = await searchSignifyProducts(searchParams);
        return result.data;
    } catch (error) {
        return {
            freeTextSearch: searchParams.query ?? '',
            pagination: { currentPage: 0, pageSize: searchParams.pageSize ?? 20, sort: '', totalPages: 0, totalResults: 0 },
            products: [],
        };
    }
};



/**
 * Client-side deadline for the getSignifyProductAndPriceAndAvailability callable, in
 * milliseconds. The callable runs with `timeoutSeconds: 150`; the Firebase JS SDK defaults to
 * 70s, which would abort the client while the server is still fetching. Sits just above the
 * server deadline so the server always loses the race.
 */
const PRODUCT_AND_PRICE_AND_AVAILABILITY_CLIENT_TIMEOUT_MS = 160_000;

/**
 * Get both product details and price from Signify API via Firebase Function
 *
 * @param productCode - The 12NC product code (e.g., '000000911401837681')
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @param customerId - Optional; when provided (e.g. from modal), used for the API call instead of env default
 * @param distributorId - Optional; when provided (e.g. from modal selection), used for the API call instead of env default
 * @returns Promise resolving to combined product and price data
 * @throws Error if the product lookup fails (price errors are included in response)
 */
export const getProductAndPriceAndAvailability = async (
    productCode: string,
    fields: 'BASIC' | 'DEFAULT' | 'FULL' = 'FULL',
    customerId?: string,
    distributorId?: string,
    configurationId?: string
): Promise<ProductAndPriceAndAvailabilityResponse> => {
    try {
        const getSignifyProductAndPriceAndAvailability = httpsCallable<ProductLookupRequest, ProductAndPriceAndAvailabilityResponse>(
            functions,
            'getSignifyProductAndPriceAndAvailability',
            { timeout: PRODUCT_AND_PRICE_AND_AVAILABILITY_CLIENT_TIMEOUT_MS }
        );

        const payload: ProductLookupRequest = { productCode, fields };
        if (customerId?.trim()) payload.customerId = customerId.trim();
        if (distributorId?.trim()) payload.distributorId = distributorId.trim();
        if (configurationId?.trim()) payload.configurationId = configurationId.trim();

        const result = await getSignifyProductAndPriceAndAvailability(payload);

        return result.data;
    } catch (error) {
        console.error('Product, price, and availability lookup failed:', error);
        throw error;
    }
};

/**
 * Get list of customers from Signify API
 * 
 * @param params - Request parameters for customer search
 * @returns Promise resolving to list of customers
 * @throws Error if the customer lookup fails
 */
export const getCustomers = async (params?: GetSignifyCustomersRequest): Promise<SignifyCustomersResponse> => {
    try {
        const getSignifyCustomers = httpsCallable<GetSignifyCustomersRequest, SignifyCustomersResponse>(
            functions,
            'getSignifyCustomers'
        );
        
        const result = await getSignifyCustomers(params || {});
        return result.data;
    } catch (error) {
        console.error('Customer lookup failed:', error);
        throw error;
    }
};

/**
 * Get list of distributors for a specific customer from Signify API
 * 
 * @param customerId - The customer ID to get distributors for
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to list of distributors
 * @throws Error if the distributor lookup fails
 */
export const getDistributors = async (customerId: string, fields: 'BASIC' | 'DEFAULT' | 'FULL' = 'FULL'): Promise<SignifyDistributorsResponse> => {
    try {
        const getSignifyDistributors = httpsCallable<GetSignifyDistributorsRequest, SignifyDistributorsResponse>(
            functions,
            'getSignifyDistributors'
        );
        
        const result = await getSignifyDistributors({ customerId, fields });
        return result.data;
    } catch (error) {
        console.error('Distributor lookup failed:', error);
        throw error;
    }
};

/**
 * Get product availability from Signify API
 * 
 * @param productCode - The 12NC product code (e.g., '000000911401837681')
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to product availability data
 * @throws Error if the availability lookup fails
 */
export const getProductAvailability = async (productCode: string, fields: 'BASIC' | 'DEFAULT' | 'FULL' = 'DEFAULT'): Promise<SignifyProductAvailabilityResponse> => {
    try {
        const getSignifyProductAvailability = httpsCallable<GetSignifyProductAvailabilityRequest, SignifyProductAvailabilityResponse>(
            functions,
            'getSignifyProductAvailability'
        );
        
        const result = await getSignifyProductAvailability({ productCode, fields });
        return result.data;
    } catch (error) {
        console.error('Product availability lookup failed:', error);
        throw error;
    }
};

/**
 * Get detailed information about a specific distributor
 * 
 * @param customerId - The customer ID
 * @param distributorId - The distributor ID  
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to distributor details
 * @throws Error if the distributor details lookup fails
 */
export const getDistributorDetails = async (customerId: string, distributorId: string, fields: 'BASIC' | 'DEFAULT' | 'FULL' = 'FULL'): Promise<SignifyDistributorDetails> => {
    try {
        const getSignifyDistributorDetails = httpsCallable<GetSignifyDistributorDetailsRequest, SignifyDistributorDetails>(
            functions,
            'getSignifyDistributorDetails'
        );
        
        const result = await getSignifyDistributorDetails({ customerId, distributorId, fields });
        return result.data;
    } catch (error) {
        console.error('Distributor details lookup failed:', error);
        throw error;
    }
};

/**
 * Get configured products (create configuration session) from Signify API
 * 
 * @param customerId - The customer ID
 * @param distributorId - The distributor ID
 * @param productId - The product ID to configure
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to configuration session with product options
 * @throws Error if the configuration creation fails
 */
export const getConfiguredProducts = async (
    customerId: string,
    distributorId: string,
    productId: string,
    fields?: 'BASIC' | 'DEFAULT' | 'FULL'
): Promise<SignifyConfiguredProductsResponse> => {
    try {
        const getConfiguredProducts = httpsCallable<GetConfiguredProductsRequest, SignifyConfiguredProductsResponse>(
            functions,
            'getConfiguredProducts'
        );
        
        const result = await getConfiguredProducts({ customerId, distributorId, productId, fields });
        return result.data;
    } catch (error) {
        console.error('Configuration creation failed:', error);
        throw error;
    }
};

/**
 * Retrieve an existing configuration session from Signify API
 * 
 * @param customerId - The customer ID
 * @param distributorId - The distributor ID
 * @param sessionId - The configuration session ID
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to configuration session details
 * @throws Error if the session retrieval fails
 */
export const getConfigurationSession = async (
    customerId: string,
    distributorId: string,
    sessionId: string,
    fields?: 'BASIC' | 'DEFAULT' | 'FULL'
): Promise<SignifyConfigurationSessionResponse> => {
    try {
        const getConfigurationSession = httpsCallable<GetConfigurationSessionRequest, SignifyConfigurationSessionResponse>(
            functions,
            'getConfigurationSession'
        );
        
        const result = await getConfigurationSession({ customerId, distributorId, sessionId, fields });
        return result.data;
    } catch (error) {
        console.error('Configuration session retrieval failed:', error);
        throw error;
    }
};

/**
 * Auto-configure a product from catalog code
 * 
 * @param customerId - The customer ID
 * @param distributorId - The distributor ID
 * @param catalogCode - The catalog code for auto-configuration
 * @param createSession - Whether to create a configuration session
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to auto-configured product details with pricing
 * @throws Error if the auto-configuration fails
 */
export const autoConfigureProduct = async (
    customerId: string,
    distributorId: string,
    catalogCode: string,
    createSession?: boolean,
    fields?: 'BASIC' | 'DEFAULT' | 'FULL'
): Promise<SignifyAutoConfigureResponse> => {
    try {
        const autoConfigureProduct = httpsCallable<AutoConfigureProductRequest, SignifyAutoConfigureResponse>(
            functions,
            'autoConfigureProduct'
        );
        
        /**
         * `createSession` and `fields` are spread conditionally, never spelled: the
         * callable client encodes a property whose value is `undefined` as `null`
         * and only drops an absent key, so a spelled `createSession` reached the
         * function as `null` (SYD-2445).
         */
        const result = await autoConfigureProduct({
            customerId,
            distributorId,
            catalogCode,
            ...(createSession !== undefined ? { createSession } : {}),
            ...(fields ? { fields } : {}),
        });
        return result.data;
    } catch (error) {
        console.error('Auto-configuration failed:', error);
        throw error;
    }
};

/**
 * Get product information (without price) from Signify API
 * 
 * @param productCode - The 12NC product code (e.g., '000000911401837681')
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to product information
 * @throws Error if the product lookup fails
 */
export const getProduct = async (productCode: string, fields: 'BASIC' | 'DEFAULT' | 'FULL' = 'FULL'): Promise<ProductLookupResponse> => {
    try {
        const getSignifyProduct = httpsCallable<ProductLookupRequest, ProductLookupResponse>(
            functions,
            'getSignifyProduct'
        );
        
        const result = await getSignifyProduct({ productCode, fields });
        return result.data;
    } catch (error) {
        console.error('Product lookup failed:', error);
        throw error;
    }
};

/**
 * Get product price from Signify API
 * 
 * @param productCode - The 12NC product code (e.g., '000000911401837681')
 * @param fields - Field level to return (BASIC, DEFAULT, FULL)
 * @returns Promise resolving to product price information
 * @throws Error if the price lookup fails
 */
export const getProductPrice = async (productCode: string, fields: 'BASIC' | 'DEFAULT' | 'FULL' = 'DEFAULT'): Promise<SignifyProductPriceResponse> => {
    try {
        const getSignifyProductPrice = httpsCallable<GetSignifyProductPriceRequest, SignifyProductPriceResponse>(
            functions,
            'getSignifyProductPrice'
        );
        
        const result = await getSignifyProductPrice({ productCode, fields });
        return result.data;
    } catch (error) {
        console.error('Product price lookup failed:', error);
        throw error;
    }
};

/**
 * Create a quote in Signify API
 * 
 * @param customerId - The customer ID
 * @param distributorId - The distributor ID
 * @param quoteData - The quote data including products and project details
 * @returns Promise resolving to created quote response
 * @throws Error if quote creation fails
 */
export const createQuote = async (customerId: string, distributorId: string, quoteData: any): Promise<SignifyQuoteResponse> => {
    try {
        const createQuote = httpsCallable<CreateQuoteRequest, SignifyQuoteResponse>(
            functions,
            'createQuote'
        );
        
        const result = await createQuote({ customerId, distributorId, quoteData });
        return result.data;
    } catch (error) {
        console.error('Quote creation failed:', error);
        throw error;
    }
};

/**
 * Get quote details from Signify API
 * 
 * @param quoteId - The quote ID
 * @param revision - Optional revision number
 * @returns Promise resolving to quote details
 * @throws Error if quote retrieval fails
 */
export const getQuote = async (quoteId: string, revision?: number): Promise<SignifyQuoteDetailsResponse> => {
    try {
        const getQuote = httpsCallable<GetQuoteRequest, SignifyQuoteDetailsResponse>(
            functions,
            'getQuote'
        );
        
        const result = await getQuote({ quoteId, revision });
        return result.data;
    } catch (error) {
        console.error('Quote retrieval failed:', error);
        throw error;
    }
};

/**
 * Ask Signify what it currently knows about the order behind one communication-log
 * record. Observation only: the callable writes nothing and the record's outcome is
 * unchanged, so the answer's four cases — matched, no match, search failed, nothing
 * to search on — are handed back untouched for a person to interpret. A thrown
 * error is rethrown rather than folded into a fallback value: a failure to ask must
 * never read as an answer.
 *
 * @param params - The record's parent as validated segments (a form version or an
 *                 entity record), the log field id and the log record's id. The
 *                 callable builds the Firestore path itself and authorizes that
 *                 parent, so a path is never sent from here.
 * @returns Promise resolving to what the search reported
 * @throws Error if the check could not be performed at all
 */
export const checkSignifyOrderStatus = async (
    params: CheckSignifyOrderStatusRequest,
): Promise<SignifyOrderStatusCheck> => {
    try {
        const checkOrderStatus = httpsCallable<CheckSignifyOrderStatusRequest, SignifyOrderStatusCheck>(
            functions,
            'checkSignifyOrderStatus'
        );

        const result = await checkOrderStatus(params);
        return result.data;
    } catch (error) {
        console.error('Signify order status check failed:', error);
        throw error;
    }
};

export const issueSignifyToken = async (email: string, authcode: string): Promise<{ token: string; expiresAt: number }> => {
    try {
        const fn = httpsCallable<IssueSignifyTokenRequest, { token: string; expiresAt: number }>(
            functions,
            'issueSignifyToken'
        );
        const result = await fn({ email, authcode });
        return result.data;
    } catch (error) {
        console.error('Signify token issuance failed:', error);
        throw error;
    }
};

export const refreshSignifyTokenManual = async (): Promise<{ token: string; expiresAt: number }> => {
    try {
        const fn = httpsCallable<object, { token: string; expiresAt: number }>(
            functions,
            'refreshSignifyToken'
        );
        const result = await fn({});
        return result.data;
    } catch (error) {
        console.error('Signify token refresh failed:', error);
        throw error;
    }
};
/**
 * Start a multi-part Signify fetch job. The callable writes the job ledger and enqueues one task;
 * the parts resolve asynchronously and the modal follows them through the job's subcollection.
 *
 * @param request - Target key, distributor and the operator's staged entries, in their order
 * @returns Promise resolving to the id of the job that was created
 * @throws Error if the job could not be created, so the draft can be left intact for a retry
 */
export const startSignifyFetchJob = async (
    request: StartSignifyFetchJobRequest
): Promise<StartSignifyFetchJobResponse> => {
    try {
        const startFetchJob = httpsCallable<StartSignifyFetchJobRequest, StartSignifyFetchJobResponse>(
            functions,
            'startSignifyFetchJob'
        );

        const result = await startFetchJob(request);
        return result.data;
    } catch (error) {
        console.error('Signify fetch job start failed:', error);
        throw error;
    }
};

/**
 * Resolve the ambiguous parts of a fetch job from the operator's picks and return every failed
 * part to `pending`. The callable enqueues a dispatch whether or not anything was reset.
 *
 * @param request - The job id and, optionally, one candidate code per ambiguous part
 * @returns Promise resolving to the job id and how many parts were returned to `pending`
 * @throws Error if the retry was refused, so the picks can be left on screen to correct
 */
export const retrySignifyFetchJob = async (
    request: RetrySignifyFetchJobRequest
): Promise<RetrySignifyFetchJobResponse> => {
    try {
        const retryFetchJob = httpsCallable<RetrySignifyFetchJobRequest, RetrySignifyFetchJobResponse>(
            functions,
            'retrySignifyFetchJob'
        );

        const result = await retryFetchJob(request);
        return result.data;
    } catch (error) {
        console.error('Signify fetch job retry failed:', error);
        throw error;
    }
};

/**
 * Cancel a fetch job. Parts already in flight finish; pending parts are left untouched.
 *
 * @param request - The job id to cancel
 * @returns Promise resolving to the cancelled job id
 * @throws Error if the cancel was refused, so the operator can be told the fetch is still running
 */
export const cancelSignifyFetchJob = async (
    request: CancelSignifyFetchJobRequest
): Promise<CancelSignifyFetchJobResponse> => {
    try {
        const cancelFetchJob = httpsCallable<CancelSignifyFetchJobRequest, CancelSignifyFetchJobResponse>(
            functions,
            'cancelSignifyFetchJob'
        );

        const result = await cancelFetchJob(request);
        return result.data;
    } catch (error) {
        console.error('Signify fetch job cancel failed:', error);
        throw error;
    }
};
